import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTrash, FaPlus, FaTimes, FaChevronDown } from 'react-icons/fa';
import Button from '../shared/Button';
import axios from 'axios';
import VariableInput from './VariableInput';

// Multi-select dropdown with checkboxes
const MultiSelectDropdown = ({ options, value = [], onChange, placeholder = 'Select...', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (optionValue) => {
    if (disabled) return;
    let newValue;
    if (optionValue === 'all') {
      // If "All" is selected, clear all other selections
      newValue = value.includes('all') ? [] : ['all'];
    } else {
      // Remove "all" if it's selected and toggle the option
      const filteredValue = value.filter(v => v !== 'all');
      if (filteredValue.includes(optionValue)) {
        newValue = filteredValue.filter(v => v !== optionValue);
      } else {
        newValue = [...filteredValue, optionValue];
      }
    }
    onChange(newValue);
  };

  const getDisplayText = () => {
    if (!value || value.length === 0) return placeholder;
    if (value.includes('all')) {
      const allOption = options.find(opt => opt.value === 'all');
      return allOption ? allOption.label.trim() : 'All Selected';
    }
    if (value.length === 1) {
      const option = options.find(opt => opt.value === value[0]);
      return option ? option.label.trim() : placeholder;
    }
    return `${value.length} selected`;
  };

  return (
    <div className="multi-select-dropdown" ref={dropdownRef}>
      <div 
        className={`multi-select-trigger ${disabled ? 'disabled' : ''}`} 
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{ opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <span>{getDisplayText()}</span>
        <FaChevronDown className={`dropdown-icon ${isOpen ? 'open' : ''}`} />
      </div>
      
      {isOpen && !disabled && (
        <div className="multi-select-options">
          {options.map((option) => (
            <label key={option.value} className="multi-select-option">
              <input
                type="checkbox"
                checked={value.includes(option.value)}
                onChange={() => handleToggle(option.value)}
                disabled={disabled}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const NODE_VARIABLE_TEMPLATES = {
  default: [
    { path: '', label: 'Full output (JSON)' },
    { path: 'data', label: 'Data' },
    { path: 'text', label: 'Text / Body' },
    { path: 'result', label: 'Result' },
    { path: 'status', label: 'Status' },
    { path: 'message', label: 'Message' },
    { path: 'subject', label: 'Subject' },
    { path: 'body', label: 'Body' },
    { path: 'prompt', label: 'Prompt' }
  ],
  'cognition.generation': [
    { path: 'text', label: 'Generated Text' },
    { path: 'prompt', label: 'Resolved Prompt' },
    { path: 'tokensUsed', label: 'Tokens Used' },
    { path: 'status', label: 'Generation Status' },
    { path: 'model', label: 'Model' }
  ],
  'cognition.conditions': [
    { path: 'result', label: 'Condition Result' },
    { path: 'branch', label: 'Branch / Outcome' },
    { path: 'filtered', label: 'Filtered Records' }
  ],
  'action.email': [
    { path: 'subject', label: 'Email Subject' },
    { path: 'body', label: 'Email Body' },
    { path: 'messageId', label: 'Message ID' },
    { path: 'draftId', label: 'Draft ID' },
    { path: 'action', label: 'Email Action (send/draft)' }
  ],
  'action.messaging': [
    { path: 'message', label: 'Message Content' },
    { path: 'platform', label: 'Messaging Platform' },
    { path: 'channel', label: 'Channel' },
    { path: 'timestamp', label: 'Timestamp' },
    { path: 'messageId', label: 'Message ID' }
  ],
  'action.documents': [
    { path: 'title', label: 'Document Title' },
    { path: 'url', label: 'Document URL' },
    { path: 'documentId', label: 'Document ID' },
    { path: 'destination', label: 'Destination' }
  ],
  'action.velox': [
    { path: 'summary', label: 'Velox Summary' },
    { path: 'battleCardId', label: 'Battle Card ID' },
    { path: 'accountBriefId', label: 'Account Brief ID' },
    { path: 'status', label: 'Velox Status' }
  ],
  'trigger.buyer_intent': [
    { path: 'filteredData.company.companyName', label: 'Company Name (Variables)' },
    { path: 'filteredData.company.industry', label: 'Industry (Variables)' },
    { path: 'filteredData.company.dealStage', label: 'Deal Stage (Variables)' },
    { path: 'filteredData.summary.totalTranscripts', label: 'Total Transcripts (Variables)' },
    { path: 'filteredData.summary.totalContacts', label: 'Total Contacts (Variables)' },
    { path: 'filteredData.summary.totalNewsArticles', label: 'Total News Articles (Variables)' },
    { path: 'filteredData.transcripts', label: 'Transcripts Array (Variables)' },
    { path: 'filteredData.contacts', label: 'Contacts Array (Variables)' },
    { path: 'filteredData.newsData', label: 'News Data Array (Variables)' },
    { path: 'filteredData.linkedInData', label: 'LinkedIn Data Array (Variables)' },
    { path: 'filteredData.accountBriefs', label: 'Account Briefs Array (Variables)' },
    { path: 'filteredData.insights', label: 'Insights Array (Variables)' }
  ],
  'trigger.deal_health': [
    { path: 'filteredData.company.companyName', label: 'Company Name (Variables)' },
    { path: 'filteredData.company.industry', label: 'Industry (Variables)' },
    { path: 'filteredData.company.dealStage', label: 'Deal Stage (Variables)' },
    { path: 'filteredData.summary.totalTranscripts', label: 'Total Transcripts (Variables)' },
    { path: 'filteredData.summary.totalContacts', label: 'Total Contacts (Variables)' },
    { path: 'filteredData.summary.totalNewsArticles', label: 'Total News Articles (Variables)' },
    { path: 'filteredData.transcripts', label: 'Transcripts Array (Variables)' },
    { path: 'filteredData.contacts', label: 'Contacts Array (Variables)' },
    { path: 'filteredData.newsData', label: 'News Data Array (Variables)' },
    { path: 'filteredData.linkedInData', label: 'LinkedIn Data Array (Variables)' },
    { path: 'filteredData.accountBriefs', label: 'Account Briefs Array (Variables)' },
    { path: 'filteredData.insights', label: 'Insights Array (Variables)' }
  ]
};

const SIGNAL_TRIGGER_TYPES = new Set(['trigger.buyer_intent', 'trigger.deal_health']);

const TRIGGER_OUTPUT_SHORTCUTS = [
  { value: `{{trigger.filteredData.company.companyName}}`, label: 'Company Name' },
  { value: `{{trigger.filteredData.company.industry}}`, label: 'Industry' },
  { value: `{{trigger.filteredData.company.dealStage}}`, label: 'Deal Stage' },
  { value: `{{trigger.filteredData.summary.totalTranscripts}}`, label: 'Total Transcripts' },
  { value: `{{trigger.filteredData.summary.totalContacts}}`, label: 'Total Contacts' },
  { value: `{{trigger.filteredData.summary.totalNewsArticles}}`, label: 'Total News Articles' },
  { value: `{{trigger.filteredData.transcripts}}`, label: 'Transcripts Array' },
  { value: `{{trigger.filteredData.contacts}}`, label: 'Contacts Array' },
  { value: `{{trigger.filteredData.newsData}}`, label: 'News Data Array' },
  { value: `{{trigger.filteredData.linkedInData}}`, label: 'LinkedIn Data Array' },
  { value: `{{trigger.filteredData.accountBriefs}}`, label: 'Account Briefs Array' },
  { value: `{{trigger.filteredData.insights}}`, label: 'Insights Array' },
  { value: `{{item.id}}`, label: 'Transcript ID (in array)' },
  { value: `{{item.summary}}`, label: 'Transcript Summary (in array)' },
  { value: `{{item.fileName}}`, label: 'Transcript File Name (in array)' },
  { value: `{{item.firstName}}`, label: 'Contact First Name (in array)' },
  { value: `{{item.lastName}}`, label: 'Contact Last Name (in array)' },
  { value: `{{item.email}}`, label: 'Contact Email (in array)' },
  { value: `{{item.title}}`, label: 'Contact Title (in array)' },
  { value: `{{item.title}}`, label: 'News Title (in array)' },
  { value: `{{item.description}}`, label: 'News Description (in array)' }
];

const appendTriggerOutputFields = (fields, sourceNode) => {
  if (!sourceNode) return;
  const nodeLabel = sourceNode.data?.label || 'Signal';
  TRIGGER_OUTPUT_SHORTCUTS.forEach((shortcut) => {
    fields.push({
      value: shortcut.value,
      label: `${shortcut.label} (${nodeLabel})`,
      group: nodeLabel
    });
  });
};

const appendVariableTemplatesForNode = (sourceNode, fields) => {
  if (!sourceNode) return;
  const nodeLabel = sourceNode.data?.label || sourceNode.id;
  const nodeType = sourceNode.data?.nodeType || sourceNode.type;
  const templates = [
    ...NODE_VARIABLE_TEMPLATES.default,
    ...(NODE_VARIABLE_TEMPLATES[nodeType] || [])
  ];
  const seen = new Set();

  templates.forEach((template) => {
    const templateKey = template.value || template.path || template.label;
    if (seen.has(templateKey)) return;
    seen.add(templateKey);

    const variablePath = template.path ? `.${template.path}` : '';
    const templateValue =
      typeof template.value === 'function'
        ? template.value(sourceNode)
        : `{{variables.${sourceNode.id}${variablePath}}}`;

    fields.push({
      value: templateValue,
      label: template.label.includes(nodeLabel) ? template.label : `${template.label} (${nodeLabel})`,
      group: nodeLabel
    });
  });
};

const dedupeFieldsByValue = (fields = []) => {
  const seen = new Set();
  return fields.filter((field) => {
    if (!field?.value) return false;
    const key = `${field.value}::${field.label}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const NodeConfigPanel = ({ node, nodes = [], edges = [], isOpen, onClose, onSave, onDelete, saving = false }) => {
  const [config, setConfig] = useState({});

  const ensureGenerationDefaults = (inputConfig = {}) => {
    const normalized = { ...inputConfig };
    if (!normalized.generationType) {
      normalized.generationType = 'text_generation';
    }
    if (!normalized.model) {
      normalized.model = 'gpt-4';
    }
    if (
      normalized.temperature === undefined ||
      normalized.temperature === null ||
      normalized.temperature === ''
    ) {
      normalized.temperature = '0.7';
    }
    return normalized;
  };
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  useEffect(() => {
    if (node) {
      const nodeType = node.data?.nodeType;
      const initialConfig = node.data?.config || {};
      
      if (nodeType === 'cognition.generation') {
        setConfig(ensureGenerationDefaults(initialConfig));
      } else {
        setConfig(initialConfig);
      }
    } else {
      setConfig({});
    }
  }, [node]);

  const loadAccounts = useCallback(async () => {
    try {
      setLoadingAccounts(true);
      const response = await axios.get('/api/custom-agents/trigger-data/accounts', {
        params: { limit: 100 },
        withCredentials: true
      });
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  // Get available fields from connected signal nodes
  const getAvailableFields = useCallback(() => {
    if (!node) return [];

    const fields = [];
    const incomingEdges = (edges || []).filter((edge) => edge.target === node.id);
    const hasIncomingEdges = incomingEdges.length > 0;

    if (!hasIncomingEdges) {
      if (SIGNAL_TRIGGER_TYPES.has(node.data?.nodeType)) {
        appendTriggerOutputFields(fields, node);
        appendVariableTemplatesForNode(node, fields);
      }
      return dedupeFieldsByValue(fields);
    }

    const sourceNodeIds = incomingEdges.map((edge) => edge.source);
    const sourceNodes = (nodes || []).filter((n) => sourceNodeIds.includes(n.id));

    sourceNodes.forEach((sourceNode) => {
      if (SIGNAL_TRIGGER_TYPES.has(sourceNode.data?.nodeType)) {
        appendTriggerOutputFields(fields, sourceNode);
      }
      appendVariableTemplatesForNode(sourceNode, fields);
    });

    return dedupeFieldsByValue(fields);
  }, [node, nodes, edges]);

  // Load accounts when panel opens and node needs account selection
  useEffect(() => {
    if (isOpen && node) {
      const nodeType = node.data?.nodeType;
      if (nodeType === 'trigger.buyer_intent' || nodeType === 'trigger.deal_health') {
        loadAccounts();
      }
    }
  }, [isOpen, node, loadAccounts]);

  if (!isOpen || !node) return null;

  const handleChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    const nodeType = node.data?.nodeType;
    const normalizedConfig =
      nodeType === 'cognition.generation'
        ? ensureGenerationDefaults(config)
        : config;

    onSave(node.id, normalizedConfig);
  };

  const renderConfigFields = () => {
    const nodeType = node.data?.nodeType;
    const availableFields = getAvailableFields();

    // Trigger configurations
    if (nodeType === 'trigger.buyer_intent') {
      return (
        <>
          <div className="config-field">
            <label>Trigger Type</label>
            <select
              value={config.triggerType || 'always_on'}
              onChange={(e) => handleChange('triggerType', e.target.value)}
            >
              <option value="always_on">Always On</option>
              <option value="one_time">One Time</option>
            </select>
            <small>Always On: CustomAgent runs continuously when deployed. One Time: CustomAgent runs once when deployed.</small>
          </div>

          <div className="config-field">
            <label>Intent Signal Type</label>
            <select
              value={config.intentType || 'engagement'}
              onChange={(e) => handleChange('intentType', e.target.value)}
            >
              <option value="engagement">High Engagement</option>
              <option value="competitor_mention">Competitor Mention</option>
              <option value="pricing_inquiry">Pricing Inquiry</option>
              <option value="feature_request">Feature Request</option>
              <option value="decision_maker">Decision Maker Engaged</option>
            </select>
          </div>

          <div className="config-field">
            <label>Trigger Conditions</label>
            <VariableInput
              as="textarea"
              rows={4}
              value={config.conditions || ''}
              onChange={(value) => handleChange('conditions', value)}
              placeholder="Enter conditions for triggering this customAgent"
              availableFields={availableFields}
            />
            <small>Specify additional conditions for triggering the customAgent</small>
          </div>

          <div className="config-field">
            <label>Select Account</label>
            <MultiSelectDropdown
              options={[
                { value: 'all', label: '  All Companies' },
                ...accounts.map((account) => ({
                  value: account.id,
                  label: `  ${account.companyName || account.name || 'Unnamed Company'}`
                }))
              ]}
              value={Array.isArray(config.accountId) ? config.accountId : (config.accountId ? [config.accountId] : [])}
              onChange={(value) => handleChange('accountId', value)}
              placeholder="Select accounts"
              disabled={loadingAccounts}
            />
            <small>Select one or more accounts to monitor for buyer intent signals</small>
          </div>

          <div className="config-field">
            <label>Deal Stage Filter</label>
            <MultiSelectDropdown
              options={[
                { value: 'all', label: '  All Stages' },
                { value: 'qualification', label: '  Qualification' },
                { value: 'discovery', label: '  Discovery' },
                { value: 'proposal', label: '  Proposal' },
                { value: 'negotiation', label: '  Negotiation' },
                { value: 'closing', label: '  Closing' }
              ]}
              value={Array.isArray(config.dealStageFilter) ? config.dealStageFilter : (config.dealStageFilter ? [config.dealStageFilter] : ['all'])}
              onChange={(value) => handleChange('dealStageFilter', value)}
              placeholder="Select stages"
            />
            <small>Select one or more deal stages to monitor</small>
          </div>
        </>
      );
    }

    if (nodeType === 'trigger.deal_health') {
      return (
        <>
          <div className="config-field">
            <label>Trigger Type</label>
            <select
              value={config.triggerType || 'always_on'}
              onChange={(e) => handleChange('triggerType', e.target.value)}
            >
              <option value="always_on">Always On</option>
              <option value="one_time">One Time</option>
            </select>
            <small>Always On: CustomAgent runs continuously when deployed. One Time: CustomAgent runs once when deployed.</small>
          </div>

          <div className="config-field">
            <label>Health Metric</label>
            <select
              value={config.healthMetric || 'overall'}
              onChange={(e) => handleChange('healthMetric', e.target.value)}
            >
              <option value="overall">Overall Deal Health</option>
              <option value="engagement_drop">Engagement Drop</option>
              <option value="stale_deal">Stale Deal</option>
              <option value="competitor_risk">Competitor Risk</option>
              <option value="budget_concern">Budget Concerns</option>
            </select>
          </div>

          <div className="config-field">
            <label>Trigger Condition</label>
            <VariableInput
              value={config.triggerCondition || ''}
              onChange={(value) => handleChange('triggerCondition', value)}
              placeholder="Enter trigger condition"
              availableFields={availableFields}
            />
            <small>Specify the condition that triggers this customAgent</small>
          </div>

          <div className="config-field">
            <label>Select Account</label>
            <MultiSelectDropdown
              options={[
                { value: 'all', label: '  All Companies' },
                ...accounts.map((account) => ({
                  value: account.id,
                  label: `  ${account.companyName || account.name || 'Unnamed Company'}`
                }))
              ]}
              value={Array.isArray(config.accountId) ? config.accountId : (config.accountId ? [config.accountId] : [])}
              onChange={(value) => handleChange('accountId', value)}
              placeholder="Select accounts"
              disabled={loadingAccounts}
            />
            <small>Select one or more accounts to monitor for deal health</small>
          </div>

          <div className="config-field">
            <label>Deal Stage Filter</label>
            <MultiSelectDropdown
              options={[
                { value: 'all', label: '  All Stages' },
                { value: 'qualification', label: '  Qualification' },
                { value: 'discovery', label: '  Discovery' },
                { value: 'proposal', label: '  Proposal' },
                { value: 'negotiation', label: '  Negotiation' },
                { value: 'closing', label: '  Closing' }
              ]}
              value={Array.isArray(config.stageFilter) ? config.stageFilter : (config.stageFilter ? [config.stageFilter] : ['all'])}
              onChange={(value) => handleChange('stageFilter', value)}
              placeholder="Select stages"
            />
            <small>Select one or more deal stages to monitor</small>
          </div>

          <div className="config-field">
            <label>Check Frequency</label>
            <select
              value={config.checkFrequency || 'daily'}
              onChange={(e) => handleChange('checkFrequency', e.target.value)}
            >
              <option value="realtime">Real-time</option>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
        </>
      );
    }

    // Cognition configurations
    if (nodeType === 'cognition.conditions') {
      const conditionType = config.conditionType || 'if_else';

      return (
        <>
          <div className="config-field">
            <label>Condition Type</label>
            <select
              value={conditionType}
              onChange={(e) => handleChange('conditionType', e.target.value)}
            >
              <option value="if_else">If/Else</option>
              <option value="filter">Filter</option>
            </select>
          </div>

          {conditionType === 'if_else' && (
            <>
              <div className="config-field">
                <label>Left Value</label>
                {availableFields.length > 0 ? (
                  <>
                    <select
                      value={config.leftValue || ''}
                      onChange={(e) => handleChange('leftValue', e.target.value)}
                      onBlur={(e) => {
                        // Allow custom input if user types
                        if (e.target.value && !availableFields.find(f => f.value === e.target.value)) {
                          // Keep custom value
                        }
                      }}
                    >
                      <option value="">-- Select Field or Enter Custom --</option>
                      {availableFields.map((field, idx) => (
                        <option key={idx} value={field.value}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                    <VariableInput
                      value={config.leftValue || ''}
                      onChange={(value) => handleChange('leftValue', value)}
                      placeholder="Or enter custom: {{trigger.status}}"
                      style={{ marginTop: '8px' }}
                      availableFields={availableFields}
                    />
                  </>
                ) : (
                  <VariableInput
                    value={config.leftValue || ''}
                    onChange={(value) => handleChange('leftValue', value)}
                    placeholder="{{trigger.status}} or {{trigger.filteredData.company.dealStage}}"
                    availableFields={availableFields}
                  />
                )}
                <small>Select from available signal fields or enter a custom variable path</small>
              </div>
              <div className="config-field">
                <label>Operator</label>
                <select
                  value={config.operator || 'equals'}
                  onChange={(e) => handleChange('operator', e.target.value)}
                >
                  <option value="equals">Equals</option>
                  <option value="notEquals">Not Equals</option>
                  <option value="greaterThan">Greater Than</option>
                  <option value="lessThan">Less Than</option>
                  <option value="contains">Contains</option>
                </select>
              </div>
              <div className="config-field">
                <label>Right Value</label>
                <VariableInput
                  value={config.rightValue || ''}
                  onChange={(value) => handleChange('rightValue', value)}
                  placeholder="active"
                  availableFields={availableFields}
                />
              </div>
            </>
          )}

          {conditionType === 'filter' && (() => {
            const filters = config.filters || [{ leftValue: '', operator: 'equals', rightValue: '' }];
            
            const addFilter = () => {
              const newFilters = [...filters, { leftValue: '', operator: 'equals', rightValue: '' }];
              handleChange('filters', newFilters);
            };

            const removeFilter = (index) => {
              const newFilters = filters.filter((_, i) => i !== index);
              handleChange('filters', newFilters.length > 0 ? newFilters : [{ leftValue: '', operator: 'equals', rightValue: '' }]);
            };

            const updateFilter = (index, field, value) => {
              const newFilters = [...filters];
              newFilters[index] = { ...newFilters[index], [field]: value };
              handleChange('filters', newFilters);
            };

            return (
              <>
                <div className="config-field">
                  <label>Data Source (Array)</label>
                  {availableFields.length > 0 ? (
                    <>
                      <select
                        value={config.dataSource || ''}
                        onChange={(e) => handleChange('dataSource', e.target.value)}
                      >
                        <option value="">-- Select Array Field or Enter Custom --</option>
                        {availableFields
                          .filter(f => f.label.includes('Array') || 
                                  f.value.includes('transcripts') || f.value.includes('contacts') || 
                                  f.value.includes('newsData') || f.value.includes('linkedInData') ||
                                  f.value.includes('accountBriefs') || f.value.includes('insights'))
                          .map((field, idx) => (
                            <option key={idx} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                      </select>
                      <VariableInput
                        value={config.dataSource || ''}
                        onChange={(value) => handleChange('dataSource', value)}
                        placeholder="Or enter custom: {{trigger.items}}"
                        style={{ marginTop: '8px' }}
                        availableFields={availableFields}
                      />
                    </>
                  ) : (
                    <VariableInput
                      value={config.dataSource || ''}
                      onChange={(value) => handleChange('dataSource', value)}
                      placeholder="{{trigger.filteredData.transcripts}} or {{variables.nodeId.result}}"
                      availableFields={availableFields}
                    />
                  )}
                  <small>Select an array field from connected signal nodes or enter a custom variable path</small>
                </div>

                <div className="config-field">
                  <label>Filter Logic</label>
                  <select
                    value={config.filterLogic || 'AND'}
                    onChange={(e) => handleChange('filterLogic', e.target.value)}
                  >
                    <option value="AND">All conditions must match (AND)</option>
                    <option value="OR">Any condition can match (OR)</option>
                  </select>
                </div>

                <div className="config-field">
                  <label>Filter Conditions</label>
                  <div className="filter-conditions-list">
                    {filters.map((filter, index) => (
                      <div key={index} className="filter-condition-item">
                        <div className="filter-condition-header">
                          <span className="filter-condition-label">Condition {index + 1}</span>
                          {filters.length > 1 && (
                            <button
                              type="button"
                              className="remove-filter-btn"
                              onClick={() => removeFilter(index)}
                              title="Remove condition"
                            >
                              <FaTimes />
                            </button>
                          )}
                        </div>
                        
                        <div className="filter-condition-fields">
                          <div className="filter-field">
                            <label>Field/Value</label>
                            {availableFields.length > 0 ? (
                              <>
                                <select
                                  value={filter.leftValue || ''}
                                  onChange={(e) => updateFilter(index, 'leftValue', e.target.value)}
                                >
                                  <option value="">-- Select Field or Enter Custom --</option>
                                  {availableFields.map((field, idx) => (
                                    <option key={idx} value={field.value}>
                                      {field.label}
                                    </option>
                                  ))}
                                </select>
                                <VariableInput
                                  value={filter.leftValue || ''}
                                  onChange={(value) => updateFilter(index, 'leftValue', value)}
                                  placeholder="Or enter custom: {{item.status}}"
                                  style={{ marginTop: '8px' }}
                                  availableFields={availableFields}
                                />
                              </>
                            ) : (
                              <VariableInput
                                value={filter.leftValue || ''}
                                onChange={(value) => updateFilter(index, 'leftValue', value)}
                                placeholder="{{item.status}} or {{item.firstName}}"
                                availableFields={availableFields}
                              />
                            )}
                          </div>
                          
                          <div className="filter-field">
                            <label>Operator</label>
                            <select
                              value={filter.operator || 'equals'}
                              onChange={(e) => updateFilter(index, 'operator', e.target.value)}
                            >
                              <option value="equals">Equals</option>
                              <option value="notEquals">Not Equals</option>
                              <option value="greaterThan">Greater Than</option>
                              <option value="lessThan">Less Than</option>
                              <option value="greaterThanOrEqual">Greater Than or Equal</option>
                              <option value="lessThanOrEqual">Less Than or Equal</option>
                              <option value="contains">Contains</option>
                              <option value="notContains">Not Contains</option>
                              <option value="startsWith">Starts With</option>
                              <option value="endsWith">Ends With</option>
                              <option value="isEmpty">Is Empty</option>
                              <option value="isNotEmpty">Is Not Empty</option>
                            </select>
                          </div>
                          
                          <div className="filter-field">
                            <label>Compare To</label>
                            <VariableInput
                              value={filter.rightValue || ''}
                              onChange={(value) => updateFilter(index, 'rightValue', value)}
                              placeholder="active"
                              availableFields={availableFields}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    type="button"
                    className="add-filter-btn"
                    onClick={addFilter}
                  >
                    <FaPlus /> Add Filter Condition
                  </button>
                </div>

                <div className="config-field">
                <label>Output Name (Optional)</label>
                <VariableInput
                  value={config.outputName || ''}
                  onChange={(value) => handleChange('outputName', value)}
                  placeholder="filteredItems"
                  availableFields={availableFields}
                />
                  <small>Name for the filtered results (default: "filtered")</small>
                </div>
              </>
            );
          })()}
        </>
      );
    }

    if (nodeType === 'cognition.generation') {
      const generationType = config.generationType || 'text_generation';

      return (
        <>
          <div className="config-field">
            <label>Generation Type</label>
            <select
              value={generationType}
              onChange={(e) => handleChange('generationType', e.target.value)}
            >
              <option value="text_generation">Text Generation</option>
              <option value="alerts">Alerts</option>
            </select>
          </div>

          {generationType === 'text_generation' && (
            <>
              <div className="config-field">
                <label>Text Generation Prompt</label>
                <VariableInput
                  as="textarea"
                  rows={6}
                  value={config.prompt || ''}
                  onChange={(value) => handleChange('prompt', value)}
                  placeholder="Write an email to {{trigger.contactName}} about..."
                  availableFields={availableFields}
                />
                <small>Use {`{{variable}}`} syntax for dynamic content</small>
              </div>
              <div className="config-field">
                <label>Model</label>
                <select
                  value={config.model || 'gpt-4'}
                  onChange={(e) => handleChange('model', e.target.value)}
                >
                  <option value="gpt-4">GPT-4</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                </select>
              </div>
              <div className="config-field">
                <label>Temperature</label>
                <input
                  type="number"
                  min="0"
                  max="2"
                  step="0.1"
                  value={config.temperature || '0.7'}
                  onChange={(e) => handleChange('temperature', e.target.value)}
                />
                <small>Lower = more focused, Higher = more creative (0-2)</small>
              </div>
            </>
          )}

          {generationType === 'alerts' && (
            <>
              <div className="config-field">
                <label>Alert Channel</label>
                <select
                  value={config.alertChannel || 'slack'}
                  onChange={(e) => handleChange('alertChannel', e.target.value)}
                >
                  <option value="slack">Slack</option>
                  <option value="email">Email</option>
                </select>
              </div>

              {config.alertChannel === 'slack' && (
                <div className="config-field">
                  <label>Slack Channel ID</label>
                  <VariableInput
                    value={config.channelId || ''}
                    onChange={(value) => handleChange('channelId', value)}
                    placeholder="C1234567890"
                    availableFields={availableFields}
                  />
                </div>
              )}

              {config.alertChannel === 'email' && (
                <div className="config-field">
                  <label>Recipient Email</label>
                  <VariableInput
                    type="email"
                    value={config.recipient || ''}
                    onChange={(value) => handleChange('recipient', value)}
                    placeholder="team@company.com"
                    availableFields={availableFields}
                  />
                </div>
              )}

              <div className="config-field">
                <label>Alert Message Prompt</label>
                <VariableInput
                  as="textarea"
                  rows={4}
                  value={config.message || ''}
                  onChange={(value) => handleChange('message', value)}
                  placeholder="New battle card created for {{variables.node_xxx.competitorName}}"
                  availableFields={availableFields}
                />
                <small>Use {`{{variable}}`} syntax for dynamic content</small>
              </div>
            </>
          )}
        </>
      );
    }

    // Action configurations
    if (nodeType === 'action.email') {
      const emailAction = config.emailAction || 'send';

      return (
        <>
          <div className="config-field">
            <label>Email Action</label>
            <select
              value={emailAction}
              onChange={(e) => handleChange('emailAction', e.target.value)}
            >
              <option value="send">Send Email</option>
              <option value="draft">Create Draft</option>
            </select>
          </div>

          <div className="config-field">
            <label>To</label>
            {availableFields.length > 0 ? (
              <>
                <select
                  value={config.to || ''}
                  onChange={(e) => handleChange('to', e.target.value)}
                >
                  <option value="">-- Select Email or Enter Custom --</option>
                  {availableFields
                    .filter(f => f.value.includes('email') || f.value.includes('Email'))
                    .map((field, idx) => (
                      <option key={idx} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                </select>
                <VariableInput
                  value={config.to || ''}
                  onChange={(value) => handleChange('to', value)}
                  placeholder="Or enter: recipient@example.com or {{trigger.email}}"
                  style={{ marginTop: '8px' }}
                  availableFields={availableFields}
                />
              </>
            ) : (
              <VariableInput
                value={config.to || ''}
                onChange={(value) => handleChange('to', value)}
                placeholder="recipient@example.com or {{trigger.email}}"
                availableFields={availableFields}
              />
            )}
            <small>Use {`{{variable}}`} for dynamic values or select from connected nodes</small>
          </div>

          <div className="config-field">
            <label>CC (Optional)</label>
            <VariableInput
              value={config.cc || ''}
              onChange={(value) => handleChange('cc', value)}
              placeholder="cc@example.com"
              availableFields={availableFields}
            />
          </div>

          <div className="config-field">
            <label>Subject Source (Optional)</label>
            <select
              value={config.subjectSource || ''}
              onChange={(e) => handleChange('subjectSource', e.target.value)}
            >
              <option value="">-- Auto-detect from Generation node --</option>
              {nodes
                .filter(n => n.data?.nodeType === 'cognition.generation')
                .map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.data?.label || node.id}
                  </option>
                ))}
            </select>
            <small>Select Generation node for subject, or leave empty to auto-detect</small>
          </div>

          <div className="config-field">
            <label>Body Source (Optional)</label>
            <select
              value={config.bodySource || ''}
              onChange={(e) => handleChange('bodySource', e.target.value)}
            >
              <option value="">-- Auto-detect from Generation node --</option>
              {nodes
                .filter(n => n.data?.nodeType === 'cognition.generation')
                .map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.data?.label || node.id}
                  </option>
                ))}
            </select>
            <small>Select Generation node for body, or leave empty to auto-detect</small>
          </div>

          <div className="config-field">
            <label>Subject (Fallback)</label>
            <VariableInput
              value={config.subject || ''}
              onChange={(value) => handleChange('subject', value)}
              placeholder="Email subject (if not from Generation node)"
              availableFields={availableFields}
            />
            <small>Only used if Generation node is not connected</small>
          </div>

          <div className="config-field">
            <label>Body (Fallback)</label>
            <VariableInput
              as="textarea"
              rows={4}
              value={config.body || ''}
              onChange={(value) => handleChange('body', value)}
              placeholder="Email body (if not from Generation node)"
              availableFields={availableFields}
            />
            <small>Only used if Generation node is not connected</small>
          </div>
        </>
      );
    }

    if (nodeType === 'action.messaging') {
      const messagingPlatform = config.platform || 'slack';

      return (
        <>
          <div className="config-field">
            <label>Messaging Platform</label>
            <select
              value={messagingPlatform}
              onChange={(e) => handleChange('platform', e.target.value)}
            >
              <option value="slack">Slack</option>
              <option value="teams">Microsoft Teams</option>
            </select>
          </div>

          {messagingPlatform === 'slack' && (
            <div className="config-field">
              <label>Slack Channel</label>
              <VariableInput
                value={config.channel || config.channelId || ''}
                onChange={(value) => {
                  handleChange('channel', value);
                  handleChange('channelId', value);
                }}
                placeholder="#channel-name or C1234567890"
                availableFields={availableFields}
              />
              <small>Enter channel name (with #) or channel ID</small>
              <div style={{ 
                marginTop: '8px', 
                padding: '8px 12px', 
                background: '#f0f9ff', 
                borderRadius: '4px', 
                border: '1px solid #bae6fd',
                fontSize: '12px',
                color: '#0369a1'
              }}>
                <strong>💡 Note:</strong> The bot must be a member of the channel to send messages. 
                Invite the bot to your channel by typing <code style={{ 
                  background: '#e0f2fe', 
                  padding: '2px 6px', 
                  borderRadius: '3px',
                  fontFamily: 'monospace'
                }}>/invite @velox-ai</code> in the channel, or use a channel ID where the bot is already a member.
              </div>
            </div>
          )}

          {messagingPlatform === 'teams' && (
            <div className="config-field">
              <label>Teams Channel Webhook URL</label>
              <VariableInput
                value={config.webhookUrl || ''}
                onChange={(value) => handleChange('webhookUrl', value)}
                placeholder="https://outlook.office.com/webhook/..."
                availableFields={availableFields}
              />
              <small>Get webhook URL from Teams channel settings</small>
            </div>
          )}

          <div className="config-field">
            <label>Message Source (Optional)</label>
            <select
              value={config.messageSource || ''}
              onChange={(e) => handleChange('messageSource', e.target.value)}
            >
              <option value="">-- Auto-detect from Generation node --</option>
              {nodes
                .filter(n => n.data?.nodeType === 'cognition.generation')
                .map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.data?.label || node.id}
                  </option>
                ))}
            </select>
            <small>Select Generation node for message, or leave empty to auto-detect</small>
          </div>

          <div className="config-field">
            <label>Message (Fallback)</label>
            <VariableInput
              as="textarea"
              rows={4}
              value={config.message || ''}
              onChange={(value) => handleChange('message', value)}
              placeholder="Message content (if not from Generation node)"
              availableFields={availableFields}
            />
            <small>Only used if Generation node is not connected</small>
          </div>
        </>
      );
    }

    if (nodeType === 'action.documents') {
      const documentDestination = config.documentDestination || 'google_docs';

      return (
        <>
          <div className="config-field">
            <label>Document Destination</label>
            <select
              value={documentDestination}
              onChange={(e) => handleChange('documentDestination', e.target.value)}
            >
              <option value="google_docs">Google Docs</option>
            </select>
            <small>New documents will be created inside your connected Google account.</small>
          </div>

          <div className="config-field">
            <label>Document Title</label>
            <VariableInput
              value={config.documentTitle || ''}
              onChange={(value) => handleChange('documentTitle', value)}
              placeholder="e.g. Account Brief for {{trigger.filteredData.company.companyName}}"
              availableFields={availableFields}
            />
            <small>Supports {`{{variable}}`} placeholders or static text.</small>
          </div>

          <div className="config-field">
            <label>Title Source (Optional)</label>
            <select
              value={config.titleSource || ''}
              onChange={(e) => handleChange('titleSource', e.target.value)}
            >
              <option value="">-- Use document title above --</option>
              {nodes
                .filter(n => n.data?.nodeType === 'cognition.generation')
                .map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.data?.label || node.id}
                  </option>
                ))}
            </select>
            <small>Use the first sentence from a Generation node instead of the manual title.</small>
          </div>

          <div className="config-field">
            <label>Content Source (Optional)</label>
            <select
              value={config.contentSource || ''}
              onChange={(e) => handleChange('contentSource', e.target.value)}
            >
              <option value="">-- Use fallback body below --</option>
              {nodes
                .filter(n => n.data?.nodeType === 'cognition.generation')
                .map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.data?.label || node.id}
                  </option>
                ))}
            </select>
            <small>Select a Generation node to populate the Google Doc body.</small>
          </div>

          <div className="config-field">
            <label>Document Body (Fallback)</label>
            <VariableInput
              as="textarea"
              rows={6}
              value={config.body || ''}
              onChange={(value) => handleChange('body', value)}
              placeholder="Document content or template if no Generation node is connected"
              availableFields={availableFields}
            />
            <small>Used when no Generation node is connected or the selected node has no output.</small>
          </div>
        </>
      );
    }

    if (nodeType === 'action.velox') {
      const veloxAction = config.veloxAction || 'battle_card';

      // Helper function to handle multi-field inputs
      const handleMultiFieldChange = (fieldName, index, subField, value) => {
        const currentValue = config[fieldName] || [];
        const newValue = [...currentValue];
        if (!newValue[index]) {
          newValue[index] = {};
        }
        newValue[index][subField] = value;
        handleChange(fieldName, newValue);
      };

      const addMultiFieldEntry = (fieldName) => {
        const currentValue = config[fieldName] || [];
        handleChange(fieldName, [...currentValue, {}]);
      };

      const removeMultiFieldEntry = (fieldName, index) => {
        const currentValue = config[fieldName] || [];
        const newValue = currentValue.filter((_, i) => i !== index);
        handleChange(fieldName, newValue.length > 0 ? newValue : [{}]);
      };

      return (
        <>
          <div className="config-field">
            <label>Velox Action</label>
            <select
              value={veloxAction}
              onChange={(e) => handleChange('veloxAction', e.target.value)}
            >
              <option value="battle_card">Generate Battle Card</option>
              <option value="account_brief">Generate Account Brief</option>
              <option value="objection_handling">Objection Handling</option>
              <option value="proposal">Proposal</option>
            </select>
          </div>

          <div className="config-field" style={{ padding: '12px', background: '#f0f9ff', borderRadius: '4px', marginBottom: '16px' }}>
            <small style={{ color: '#0369a1', fontWeight: '500' }}>
              💡 Tip: If connected to a Buyer Intent or Deal Health trigger, company data will be automatically used for document generation.
            </small>
          </div>

          {veloxAction === 'battle_card' && (
            <>
              <div className="config-field">
                <label>Competitor Names & Types</label>
                {(config.competitors || [{}]).map((competitor, index) => (
                  <div key={index} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 'bold' }}>Competitor {index + 1}</span>
                      {(config.competitors || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeMultiFieldEntry('competitors', index)}
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <VariableInput
                        value={competitor.name || ''}
                        onChange={(value) => handleMultiFieldChange('competitors', index, 'name', value)}
                        placeholder="Competitor Name"
                        style={{ width: '100%', marginBottom: '8px' }}
                        availableFields={availableFields}
                      />
                      <select
                        value={competitor.type || ''}
                        onChange={(e) => handleMultiFieldChange('competitors', index, 'type', e.target.value)}
                        style={{ width: '100%', padding: '6px' }}
                      >
                        <option value="">Select Type</option>
                        <option value="Direct">Direct</option>
                        <option value="Indirect">Indirect</option>
                        <option value="Legacy">Legacy</option>
                      </select>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addMultiFieldEntry('competitors')}
                  style={{ marginTop: '2px', padding: '2px 2px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '8px' }}
                >
                  Add Competitor
                </button>
              </div>

              <div className="config-field">
                <label>Specific Product/Service Line</label>
                <VariableInput
                  as="textarea"
                  rows={3}
                  value={config.productLine || ''}
                  onChange={(value) => handleChange('productLine', value)}
                  placeholder="Enter the specific product or service line"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Market Segment Focus</label>
                <select
                  value={config.marketSegment || ''}
                  onChange={(e) => handleChange('marketSegment', e.target.value)}
                >
                  <option value="">Select Market Segment</option>
                  <option value="Enterprise">Enterprise</option>
                  <option value="Mid-Market">Mid-Market</option>
                  <option value="SMB">SMB</option>
                </select>
              </div>

              <div className="config-field">
                <label>Competitive Situations</label>
                {[
                  'Head-to-head evaluation',
                  'Displacement opportunity (replacing competitor)',
                  'Competitive trap (brought in late to compare)',
                  '"No decision" competitor (status quo)'
                ].map((option) => (
                  <label key={option} style={{ display: 'block', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      checked={(config.competitiveSituations || []).includes(option)}
                      onChange={(e) => {
                        const current = config.competitiveSituations || [];
                        const newValue = e.target.checked
                          ? [...current, option]
                          : current.filter(v => v !== option);
                        handleChange('competitiveSituations', newValue);
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    {option}
                  </label>
                ))}
              </div>

              <div className="config-field">
                <label>Buyer Personas Typically Involved</label>
                {['Technical', 'Financial', 'Executive'].map((option) => (
                  <label key={option} style={{ display: 'block', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      checked={(config.buyerPersonas || []).includes(option)}
                      onChange={(e) => {
                        const current = config.buyerPersonas || [];
                        const newValue = e.target.checked
                          ? [...current, option]
                          : current.filter(v => v !== option);
                        handleChange('buyerPersonas', newValue);
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    {option}
                  </label>
                ))}
              </div>

              <div className="config-field">
                <label>Common Customer Pain Points</label>
                <VariableInput
                  as="textarea"
                  rows={4}
                  value={config.customerPainPoints || ''}
                  onChange={(value) => handleChange('customerPainPoints', value)}
                  placeholder="Describe common customer pain points"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Areas Where More Information is Needed</label>
                {[
                  'Feature comparisons',
                  'Pricing differentiators',
                  'Customer testimonials',
                  'Technical limitations',
                  'Implementation differences'
                ].map((option) => (
                  <label key={option} style={{ display: 'block', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      checked={(config.informationNeeded || []).includes(option)}
                      onChange={(e) => {
                        const current = config.informationNeeded || [];
                        const newValue = e.target.checked
                          ? [...current, option]
                          : current.filter(v => v !== option);
                        handleChange('informationNeeded', newValue);
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    {option}
                  </label>
                ))}
              </div>
            </>
          )}

          {veloxAction === 'account_brief' && (
            <>
              <div className="config-field">
                <label>Company Name</label>
                {availableFields.length > 0 ? (
                  <>
                    <select
                      value={config.companyName || ''}
                      onChange={(e) => handleChange('companyName', e.target.value)}
                    >
                      <option value="">-- Select Company or Enter Custom --</option>
                      {availableFields
                        .filter(f => f.value.includes('companyName') || f.value.includes('company.companyName'))
                        .map((field, idx) => (
                          <option key={idx} value={field.value}>
                            {field.label}
                          </option>
                        ))}
                    </select>
                    <VariableInput
                      value={config.companyName || ''}
                      onChange={(value) => handleChange('companyName', value)}
                      placeholder="Or enter: {{trigger.filteredData.company.companyName}}"
                      style={{ marginTop: '8px' }}
                      availableFields={availableFields}
                    />
                  </>
                ) : (
                  <VariableInput
                    value={config.companyName || ''}
                    onChange={(value) => handleChange('companyName', value)}
                    placeholder="{{trigger.filteredData.company.companyName}} or Company Name"
                    availableFields={availableFields}
                  />
                )}
                <small>Will auto-populate from Buyer Intent/Deal Health trigger if connected</small>
              </div>

              <div className="config-field">
                <label>Current Client Relationship Status</label>
                <select
                  value={config.relationshipStatus || ''}
                  onChange={(e) => handleChange('relationshipStatus', e.target.value)}
                >
                  <option value="">Select Status (or use from trigger)</option>
                  <option value="New Prospect">New Prospect</option>
                  <option value="Existing Client">Existing Client</option>
                  <option value="Former Client">Former Client</option>
                </select>
                <small>Will use from trigger data if available</small>
              </div>

              <div className="config-field">
                <label>Deal Stage</label>
                {availableFields.length > 0 ? (
                  <>
                    <select
                      value={config.dealStage || ''}
                      onChange={(e) => handleChange('dealStage', e.target.value)}
                    >
                      <option value="">-- Select Deal Stage or Use from Trigger --</option>
                      {availableFields
                        .filter(f => f.value.includes('dealStage'))
                        .map((field, idx) => (
                          <option key={idx} value={field.value}>
                            {field.label}
                          </option>
                        ))}
                      <option value="Prospecting">Prospecting</option>
                      <option value="Qualified Lead">Qualified Lead</option>
                      <option value="Proposal Stage">Proposal Stage</option>
                      <option value="Negotiation">Negotiation</option>
                      <option value="Closed Won">Closed Won</option>
                      <option value="Closed Lost">Closed Lost</option>
                    </select>
                  </>
                ) : (
                  <select
                    value={config.dealStage || ''}
                    onChange={(e) => handleChange('dealStage', e.target.value)}
                  >
                    <option value="">Select Deal Stage</option>
                    <option value="Prospecting">Prospecting</option>
                    <option value="Qualified Lead">Qualified Lead</option>
                    <option value="Proposal Stage">Proposal Stage</option>
                    <option value="Negotiation">Negotiation</option>
                    <option value="Closed Won">Closed Won</option>
                    <option value="Closed Lost">Closed Lost</option>
                  </select>
                )}
                <small>Will auto-populate from trigger if available</small>
              </div>

              <div className="config-field">
                <label>Engagement Goals</label>
                {[
                  'Initial outreach preparation',
                  'Meeting preparation',
                  'Call follow-up research',
                  'Proposal development support'
                ].map((option) => (
                  <label key={option} style={{ display: 'block', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      checked={(config.engagementGoals || []).includes(option)}
                      onChange={(e) => {
                        const current = config.engagementGoals || [];
                        const newValue = e.target.checked
                          ? [...current, option]
                          : current.filter(v => v !== option);
                        handleChange('engagementGoals', newValue);
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    {option}
                  </label>
                ))}
              </div>

              <div className="config-field">
                <label>Key Pain Points to Focus On</label>
                <VariableInput
                  as="textarea"
                  rows={4}
                  value={config.keyPainPoints || ''}
                  onChange={(value) => handleChange('keyPainPoints', value)}
                  placeholder="Describe key pain points to focus on"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Specific Products/Services Being Targeted</label>
                <VariableInput
                  as="textarea"
                  rows={4}
                  value={config.targetProducts || ''}
                  onChange={(value) => handleChange('targetProducts', value)}
                  placeholder="Describe the specific products or services being targeted"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Competitors Being Considered</label>
                <VariableInput
                  as="textarea"
                  rows={3}
                  value={config.competitors || ''}
                  onChange={(value) => handleChange('competitors', value)}
                  placeholder="List competitors being considered"
                  availableFields={availableFields}
                />
              </div>
            </>
          )}

          {veloxAction === 'objection_handling' && (
            <>
              <div className="config-field">
                <label>Objection Category</label>
                <select
                  value={config.objectionCategory || ''}
                  onChange={(e) => handleChange('objectionCategory', e.target.value)}
                >
                  <option value="">Select Category</option>
                  <option value="Price/Budget concerns">Price/Budget concerns</option>
                  <option value="Need/Value concerns">Need/Value concerns</option>
                  <option value="Timing/Urgency concerns">Timing/Urgency concerns</option>
                  <option value="Product/Feature concerns">Product/Feature concerns</option>
                  <option value="Technical/Integration concerns">Technical/Integration concerns</option>
                  <option value="Competitive concerns">Competitive concerns</option>
                  <option value="Internal process/Decision-making concerns">Internal process/Decision-making concerns</option>
                  <option value="Risk/Security concerns">Risk/Security concerns</option>
                  <option value="Support/Service concerns">Support/Service concerns</option>
                  <option value="Custom/Other">Custom/Other</option>
                </select>
              </div>

              <div className="config-field">
                <label>Buyer Persona & Primary Motivation</label>
                <div style={{ marginBottom: '8px' }}>
                  <VariableInput
                    value={config.buyerPersonaRole || ''}
                    onChange={(value) => handleChange('buyerPersonaRole', value)}
                    placeholder="Role/Title (e.g., CTO, CFO, end-user)"
                    style={{ width: '100%', marginBottom: '8px' }}
                    availableFields={availableFields}
                  />
                  <VariableInput
                    value={config.buyerPersonaMotivation || ''}
                    onChange={(value) => handleChange('buyerPersonaMotivation', value)}
                    placeholder="Primary Motivation (e.g., ROI, technical excellence, ease of use)"
                    style={{ width: '100%' }}
                    availableFields={availableFields}
                  />
                </div>
              </div>

              <div className="config-field">
                <label>Deal Stage</label>
                <select
                  value={config.dealStage || ''}
                  onChange={(e) => handleChange('dealStage', e.target.value)}
                >
                  <option value="">Select Deal Stage</option>
                  <option value="Early Discovery">Early Discovery</option>
                  <option value="Mid-Evaluation">Mid-Evaluation</option>
                  <option value="Late Negotiation">Late Negotiation</option>
                </select>
              </div>

              <div className="config-field">
                <label>Products/Services Being Discussed</label>
                <VariableInput
                  as="textarea"
                  rows={3}
                  value={config.productsServices || ''}
                  onChange={(value) => handleChange('productsServices', value)}
                  placeholder="List the specific products or services being discussed in this deal"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Previous Interactions Summary</label>
                <VariableInput
                  as="textarea"
                  rows={3}
                  value={config.previousInteractions || ''}
                  onChange={(value) => handleChange('previousInteractions', value)}
                  placeholder="Summarize any relevant previous interactions with the prospect"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Known Hot Buttons or Priorities</label>
                <VariableInput
                  as="textarea"
                  rows={3}
                  value={config.hotButtons || ''}
                  onChange={(value) => handleChange('hotButtons', value)}
                  placeholder="List any known priorities or hot buttons for this prospect"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Communication Channel</label>
                <select
                  value={config.communicationChannel || ''}
                  onChange={(e) => handleChange('communicationChannel', e.target.value)}
                >
                  <option value="">Select Channel</option>
                  <option value="Live Meeting">Live Meeting</option>
                  <option value="Email">Email</option>
                  <option value="Phone Call">Phone Call</option>
                </select>
              </div>

              <div className="config-field">
                <label>Tone Preference</label>
                <select
                  value={config.tonePreference || ''}
                  onChange={(e) => handleChange('tonePreference', e.target.value)}
                >
                  <option value="">Select Tone</option>
                  <option value="Consultative">Consultative</option>
                  <option value="Direct">Direct</option>
                  <option value="Educational">Educational</option>
                  <option value="Empathetic">Empathetic</option>
                </select>
              </div>

              <div className="config-field">
                <label>Response Length</label>
                <select
                  value={config.responseLength || ''}
                  onChange={(e) => handleChange('responseLength', e.target.value)}
                >
                  <option value="">Select Length</option>
                  <option value="Quick Bullet Points">Quick Bullet Points</option>
                  <option value="Detailed Response">Detailed Response</option>
                </select>
              </div>
            </>
          )}

          {veloxAction === 'proposal' && (
            <>
              <div className="config-field">
                <label>Proposal Type</label>
                <select
                  value={config.proposalType || ''}
                  onChange={(e) => handleChange('proposalType', e.target.value)}
                >
                  <option value="">Select Proposal Type</option>
                  <option value="Initial Proposal">Initial Proposal</option>
                  <option value="Revised Proposal">Revised Proposal</option>
                  <option value="Final Proposal">Final Proposal</option>
                  <option value="Statement of Work (SOW)">Statement of Work (SOW)</option>
                  <option value="Request for Proposal (RFP) Response">Request for Proposal (RFP) Response</option>
                  <option value="Custom Proposal">Custom Proposal</option>
                </select>
              </div>

              <div className="config-field">
                <label>Deal Stage</label>
                <select
                  value={config.dealStage || ''}
                  onChange={(e) => handleChange('dealStage', e.target.value)}
                >
                  <option value="">Select Deal Stage</option>
                  <option value="Proposal Development">Proposal Development</option>
                  <option value="Proposal Review">Proposal Review</option>
                  <option value="Negotiation">Negotiation</option>
                  <option value="Final Approval">Final Approval</option>
                  <option value="Contract Signing">Contract Signing</option>
                </select>
              </div>

              <div className="config-field">
                <label>Client Needs & Requirements</label>
                <VariableInput
                  as="textarea"
                  rows={4}
                  value={config.clientNeeds || ''}
                  onChange={(value) => handleChange('clientNeeds', value)}
                  placeholder="Describe the client's specific needs and requirements"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Proposed Solution Overview</label>
                <VariableInput
                  as="textarea"
                  rows={4}
                  value={config.solutionOverview || ''}
                  onChange={(value) => handleChange('solutionOverview', value)}
                  placeholder="Provide an overview of the proposed solution"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Key Deliverables</label>
                <VariableInput
                  as="textarea"
                  rows={3}
                  value={config.deliverables || ''}
                  onChange={(value) => handleChange('deliverables', value)}
                  placeholder="List the key deliverables included in this proposal"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Proposed Timeline</label>
                <VariableInput
                  as="textarea"
                  rows={3}
                  value={config.timeline || ''}
                  onChange={(value) => handleChange('timeline', value)}
                  placeholder="Outline the proposed project timeline and milestones"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Pricing Structure</label>
                <VariableInput
                  as="textarea"
                  rows={3}
                  value={config.pricing || ''}
                  onChange={(value) => handleChange('pricing', value)}
                  placeholder="Describe the pricing structure and payment terms"
                  availableFields={availableFields}
                />
              </div>

              <div className="config-field">
                <label>Success Metrics & KPIs</label>
                <VariableInput
                  as="textarea"
                  rows={3}
                  value={config.successMetrics || ''}
                  onChange={(value) => handleChange('successMetrics', value)}
                  placeholder="Define how success will be measured"
                  availableFields={availableFields}
                />
              </div>
            </>
          )}

        </>
      );
    }

    // Default configuration
    return (
      <div className="config-field">
        <p>No configuration needed for this node type.</p>
      </div>
    );
  };

  return (
    <div className="node-config-panel">
      <div className="config-header">
        <h3>{node.data?.label || 'Configure Node'}</h3>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>

      <div className="config-content">
        <div className="config-section">
          <h4>Node Configuration</h4>
          {renderConfigFields()}
        </div>

      </div>

      <div className="config-footer">
        <div className="config-footer-left">
          <button
            className="btn-delete-node"
            onClick={onDelete}
            title="Delete node"
          >
            <FaTrash /> Delete
          </button>
        </div>
        <div className="config-footer-right">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }} 
            disabled={saving}
            type="button"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NodeConfigPanel;

